/* Security & Privacy settings panel — 2FA toggle, recent sessions, GDPR export.
 * Mounted in Dashboard "More" drawer (compact) or Profile page.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { toast } from "sonner";
import CollapsiblePanel from "./CollapsiblePanel";
import PrivacyDashboard from "./profile/PrivacyDashboard";
import { ShieldCheck, ShieldOff, Smartphone, Globe, Download, AlertTriangle, Check } from "lucide-react";

function relTime(iso, bg) {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return bg ? "сега" : "now";
    if (m < 60) return bg ? `${m} мин` : `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return bg ? `${h} ч` : `${h}h`;
    const days = Math.floor(h / 24);
    return bg ? `${days} дни` : `${days}d`;
  } catch { return ""; }
}

function TwoFactorSection() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [status, setStatus] = useState(null);
  const [pw, setPw] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/security/2fa/status`);
      setStatus(data);
    } catch { setStatus(null); }
  }, [API]);
  useEffect(() => { refresh(); }, [refresh]);

  const beginSetup = async () => {
    if (!pw) { toast.error(bg ? "Въведете паролата си" : "Enter your password"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/security/2fa/setup`, { password: pw });
      setChallenge(data);
      toast.success(bg ? "Код е изпратен на вашия имейл" : "Code sent to your email");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешна парола" : "Wrong password"));
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    if (!otp || otp.length < 4) return;
    setLoading(true);
    try {
      await axios.post(`${API}/security/2fa/confirm`, {
        challenge_id: challenge.challenge_id, code: otp, enable: true,
      });
      toast.success(bg ? "Двуфакторно удостоверяване е включено" : "2FA enabled");
      setChallenge(null); setOtp(""); setPw("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Невалиден код" : "Invalid code"));
    } finally { setLoading(false); }
  };

  const disable = async () => {
    if (!pw) { toast.error(bg ? "Въведете паролата си" : "Enter your password"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/security/2fa/disable`, { password: pw });
      toast.success(bg ? "2FA изключено" : "2FA disabled");
      setPw("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка" : "Error"));
    } finally { setLoading(false); }
  };

  const enabled = !!status?.enabled;

  return (
    <div className="space-y-3" data-testid="twofa-section">
      <div className="flex items-center gap-2">
        {enabled
          ? <ShieldCheck size={18} className="text-emerald-600" />
          : <ShieldOff size={18} className="text-gray-400" />}
        <div className="text-sm font-medium text-gray-900">
          {bg ? "Двуфакторно удостоверяване (email OTP)" : "Two-factor authentication (email OTP)"}
        </div>
        <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`} data-testid="twofa-status-pill">
          {enabled ? (bg ? "Включено" : "Enabled") : (bg ? "Изключено" : "Disabled")}
        </span>
      </div>
      <p className="text-xs text-gray-600 leading-snug">
        {bg
          ? "При вход ще получавате 6-цифрен код на имейла, който ще трябва да въведете след паролата. Силно препоръчително при чувствителни здравни данни."
          : "On login, you'll receive a 6-digit code in your email that you must enter after your password. Strongly recommended for sensitive health data."}
      </p>

      {!enabled && !challenge && (
        <div className="flex gap-2 items-center">
          <input
            type="password"
            placeholder={bg ? "Парола за потвърждение" : "Password to confirm"}
            value={pw}
            onChange={e => setPw(e.target.value)}
            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            data-testid="twofa-password-input"
          />
          <button
            onClick={beginSetup}
            disabled={loading}
            className="rounded-md bg-sky-600 text-white px-3 py-1.5 text-sm hover:bg-sky-700 disabled:opacity-50"
            data-testid="twofa-begin-btn"
          >
            {bg ? "Включи 2FA" : "Enable 2FA"}
          </button>
        </div>
      )}

      {!enabled && challenge && (
        <div className="flex gap-2 items-center">
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            placeholder={bg ? "6-цифрен код" : "6-digit code"}
            value={otp}
            onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm tracking-widest"
            data-testid="twofa-otp-input"
          />
          <button
            onClick={confirm}
            disabled={loading || otp.length !== 6}
            className="rounded-md bg-emerald-600 text-white px-3 py-1.5 text-sm hover:bg-emerald-700 disabled:opacity-50"
            data-testid="twofa-confirm-btn"
          >
            <Check size={14} className="inline -mt-0.5 mr-1" />{bg ? "Потвърди" : "Confirm"}
          </button>
        </div>
      )}

      {enabled && (
        <div className="flex gap-2 items-center">
          <input
            type="password"
            placeholder={bg ? "Парола за изключване" : "Password to disable"}
            value={pw}
            onChange={e => setPw(e.target.value)}
            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            data-testid="twofa-disable-password"
          />
          <button
            onClick={disable}
            disabled={loading}
            className="rounded-md border border-rose-300 text-rose-700 px-3 py-1.5 text-sm hover:bg-rose-50 disabled:opacity-50"
            data-testid="twofa-disable-btn"
          >
            {bg ? "Изключи" : "Disable"}
          </button>
        </div>
      )}
    </div>
  );
}

function SessionsSection() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/security/sessions?limit=10`)
      .then(({ data }) => setSessions(data.sessions || []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [API]);

  if (loading) return <div className="text-xs text-gray-500">{bg ? "Зареждане…" : "Loading…"}</div>;
  if (!sessions.length) return <div className="text-xs text-gray-500">{bg ? "Няма записани сесии" : "No sessions recorded"}</div>;

  return (
    <div className="space-y-2" data-testid="sessions-section">
      <div className="flex items-center gap-2">
        <Globe size={16} className="text-sky-600" />
        <div className="text-sm font-medium text-gray-900">{bg ? "Последни входове" : "Recent logins"}</div>
      </div>
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
        {sessions.map((s) => (
          <li key={s.id} className={`px-3 py-2 flex items-center gap-3 text-xs ${s.suspicious ? "bg-rose-50" : "bg-white"}`} data-testid={`session-row-${s.id}`}>
            <Smartphone size={14} className="text-gray-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-gray-900 truncate">
                <span>{s.city || s.country || (bg ? "Неизвестно" : "Unknown")}{s.country && s.city ? `, ${s.country}` : ""}</span>
                {s.suspicious && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-rose-700 bg-rose-100 rounded-full px-1.5 py-0.5" data-testid={`session-suspicious-${s.id}`}>
                    <AlertTriangle size={10} />{bg ? "Необичайно" : "Unusual"}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-gray-500 truncate">IP {s.ip} · {relTime(s.created_at, bg)}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ExportSection() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [loading, setLoading] = useState(false);

  const doExport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/security/data-export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `nutramilo-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(bg ? "Експортът е свален" : "Export downloaded");
    } catch {
      toast.error(bg ? "Грешка при експорт" : "Export failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-2" data-testid="gdpr-export-section">
      <div className="flex items-center gap-2">
        <Download size={16} className="text-indigo-600" />
        <div className="text-sm font-medium text-gray-900">{bg ? "Експорт на моите данни (GDPR чл. 15)" : "Export my data (GDPR Art. 15)"}</div>
      </div>
      <p className="text-xs text-gray-600 leading-snug">
        {bg
          ? "Сваляте JSON файл с всички ваши лични, здравни и AI-генерирани данни + одит на съгласия и входове."
          : "Download a JSON file containing all your personal, health and AI-generated data plus consent and login audits."}
      </p>
      <button
        onClick={doExport}
        disabled={loading}
        className="rounded-md bg-indigo-600 text-white px-3 py-1.5 text-sm hover:bg-indigo-700 disabled:opacity-50"
        data-testid="gdpr-export-btn"
      >
        <Download size={12} className="inline -mt-0.5 mr-1" />
        {loading ? (bg ? "Генериране…" : "Generating…") : (bg ? "Свали данните ми" : "Download my data")}
      </button>
    </div>
  );
}

export default function SecuritySettings() {
  const { language } = useLanguage();
  const bg = language === "bg";
  return (
    <CollapsiblePanel
      title={bg ? "Сигурност и поверителност" : "Security & Privacy"}
      subtitle={bg ? "2FA, сесии, GDPR експорт" : "2FA, sessions, GDPR export"}
      icon={ShieldCheck}
      iconColor="#0EA5E9"
      credits={0}
      defaultOpen={false}
      storageKey="security-settings"
      testId="security-settings-panel"
    >
      <div className="space-y-5">
        <TwoFactorSection />
        <div className="border-t border-gray-100 pt-4"><SessionsSection /></div>
        <div className="border-t border-gray-100 pt-4"><ExportSection /></div>
        <div className="border-t border-gray-100 pt-4"><PrivacyDashboard /></div>
      </div>
    </CollapsiblePanel>
  );
}
